import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile, RegularUser } from '../types/admin';
import './UserProfileEdit.css';

interface UserProfileEditProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (profile: UserProfile) => Promise<void>;
}

export const UserProfileEdit: React.FC<UserProfileEditProps> = ({ isOpen, onClose, onSave }) => {
	const { user } = useAuth();
	const regularUser = user as RegularUser;

	const [profile, setProfile] = useState<UserProfile>({
		firstName: '',
		lastName: '',
		email: '',
		phoneNumber: '',
		institution: '',
		type: 'SOCIAL' as any,
	});

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>('');
	const [successMessage, setSuccessMessage] = useState<string>('');

	useEffect(() => {
		if (regularUser && isOpen) {
			setProfile({
				firstName: regularUser.firstName,
				lastName: regularUser.lastName,
				email: regularUser.email,
				phoneNumber: '', // This would come from a more complete user object
				institution: regularUser.institution,
				type: regularUser.type,
			});
		}
	}, [regularUser, isOpen]);

	const handleInputChange = (field: keyof UserProfile, value: string) => {
		setProfile((prev) => ({ ...prev, [field]: value }));
		setError('');
		setSuccessMessage('');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setError('');
		setSuccessMessage('');

		try {
			await onSave(profile);
			setSuccessMessage('Podaci su uspešno ažurirani');
			setTimeout(() => {
				setSuccessMessage('');
				onClose();
			}, 2000);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Greška pri ažuriranju podataka');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancel = () => {
		setError('');
		setSuccessMessage('');
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className='modal-overlay'>
			<div className='modal-content profile-edit-modal'>
				<div className='modal-header'>
					<h2>Izmena korisničkih podataka</h2>
					<button className='close-button' onClick={handleCancel} disabled={isLoading}>
						×
					</button>
				</div>

				<form onSubmit={handleSubmit} className='profile-edit-form'>
					{error && <div className='alert alert-error'>{error}</div>}
					{successMessage && <div className='alert alert-success'>{successMessage}</div>}

					<div className='form-row'>
						<div className='form-group'>
							<label htmlFor='firstName'>Ime *</label>
							<input type='text' id='firstName' value={profile.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} required disabled={isLoading} />
						</div>

						<div className='form-group'>
							<label htmlFor='lastName'>Prezime *</label>
							<input type='text' id='lastName' value={profile.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} required disabled={isLoading} />
						</div>
					</div>

					<div className='form-group'>
						<label htmlFor='email'>Email adresa *</label>
						<input type='email' id='email' value={profile.email} onChange={(e) => handleInputChange('email', e.target.value)} required disabled={isLoading} />
					</div>

					<div className='form-group'>
						<label htmlFor='phoneNumber'>Broj telefona</label>
						<input type='tel' id='phoneNumber' value={profile.phoneNumber} onChange={(e) => handleInputChange('phoneNumber', e.target.value)} disabled={isLoading} />
					</div>

					<div className='form-group'>
						<label htmlFor='institution'>Ustanova *</label>
						<input type='text' id='institution' value={profile.institution} onChange={(e) => handleInputChange('institution', e.target.value)} required disabled={isLoading} />
					</div>

					<div className='form-actions'>
						<button type='button' className='btn btn-secondary' onClick={handleCancel} disabled={isLoading}>
							Otkaži
						</button>
						<button type='submit' className='btn btn-primary' disabled={isLoading}>
							{isLoading ? 'Čuvanje...' : 'Sačuvaj izmene'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
